<template>
    <v-container>
        <v-label>{{$t('user.create')}}</v-label>
        <v-form ref="form"
                v-model="isFormValid">
            <v-container>
                <v-row>
                    <v-text-field outlined
                                  :label="$t('user.email')"
                                  :placeholder="$t('user.email')"
                                  v-model="user.email"
                                  :rules="emailRules"></v-text-field>
                </v-row>
                <v-row>
                    <v-text-field outlined
                                  :label="$t('user.password')"
                                  :placeholder="$t('user.password')"
                                  v-model="user.password"
                                  :rules="passwordRules"
                                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                                  :type="showPassword ? 'text' : 'password'"
                                  @click:append="showPassword = !showPassword"></v-text-field>
                </v-row>
                <v-row>
                    <v-select outlined
                              :label="$t('user.role')"
                              :placeholder="$t('user.role')"
                              :items="permissionOptions"
                              v-model="user.permissions"
                              v-on:input="changePermission(user.permissions)" 
                              :rules="permissionRules" >
                    </v-select>
                </v-row>

                <v-row>
                    <v-select outlined
                              :label="$t('user.company')"
                              :placeholder="$t('user.company')"
                              :items="companyOptions"
                              v-model="user.companyId"
                              v-on:input="changeCompany(true)"
                              v-if="showCompany"
                              :rules="companyRules" >
                    </v-select>
                </v-row>

                <!-- <v-row>
                    <v-select outlined
                              :label="$t('user.event')"
                              :placeholder="$t('user.event')"
                              :items="eventOptions"
                              v-model="user.eventId"
                              v-if="showEvent" 
                              :rules="eventRules" >
                    </v-select>
                </v-row> -->

                <v-card class="mb-10" v-if="showEvent">
                    <v-card-title>
                        <!-- {{$t('event.guests')}}
                        <v-spacer></v-spacer>
                        <v-btn color="red" dark @click="getInvitedEvents" class="mr-3">
                            {{'revert'}}
                            <v-icon right>mdi-refresh</v-icon>
                        </v-btn>
                        <v-btn color="primary" @click="saveEventGuests">
                            {{$t('save')}}({{selectedEvents.length}})
                            <v-icon right>mdi-check</v-icon>
                        </v-btn> -->
                        {{$t('user.event')}}
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-text-field v-model="search"
                                            append-icon="mdi-magnify"
                                            :label="$t('search')"
                                            single-line
                                            hide-details>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-data-table :headers="eventHeaders"
                                    :items="eventOptions"
                                    :loading="isEventPending"
                                    :search="search"
                                    show-select
                                    v-model="selectedEvents"
                                    disable-sort>
                            <template v-slot:item.start_date="{value}">
                                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                            </template>
                            <template v-slot:item.end_date="{value}">
                                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd')}}
                            </template>
                        </v-data-table>
                    </v-card-text>
                </v-card>

                <v-row>
                    <v-textarea outlined
                                  :auto-grow="true"
                                  :label="$t('user.remark')"
                                  :placeholder="$t('user.remark')"
                                  v-model="user.remark"></v-textarea>
                </v-row>
            </v-container>
        </v-form>
        <v-row>
            <v-col>
                <v-btn color="primary" @click="saveUser" :disabled="!isFormValid || !customFormValid || !callbackIsValid">
                    {{$t('user.create')}}
                </v-btn>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import {ref, computed, watch} from '@vue/composition-api'
    import {useFind} from "feathers-vuex";
    import eConfig from "../../evention-config";
    import {DateTime} from "luxon";
    import _ from 'lodash';

    export default {
        name: 'UserAdd',
        setup(props, {root}) {
            const {User, Company, Event, EventUser} = root.$FeathersVuex.api;
            const user = new User();

            const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            const emailRules = ref([
                value => !!value.match(emailRegex) || 'Invalid Email Address',
            ]);

            const passwordRules = ref([
                value => !!value || 'Required',
                value => (value || '').length >= 8 || 'Min 8 characters',
                value => !!value.match(/[A-Za-z]/g) || 'Must contain at least one letter',
            ]);
            const permissionRules = ref([
                value => !!value || 'Required',
            ]);
            const companyRules = ref([
                value => !!value || 'Required',
            ]);

            const isFormValid = ref(false);
            const customFormValid = ref(true);
            const showPassword = ref(false);
            const showCompany = ref(false);
            const showEvent = ref(false);
            const callbackIsValid = ref(true);
            const ePermission = eConfig.user.permission;
            const permissionOptions = Object.values(ePermission);

            const saveUser = () => {
                switch (user.permissions) {
                    case ePermission.super:
                        user.companyId = null;
                        selectedEvents.value = [];
                        break;
                    case ePermission.company:
                        selectedEvents.value = [];
                        break;
                    case ePermission.event:
                        break;
                    default:
                        console.error("Different Permissions");
                        return;
                }

                user.save().then((user) => {
                    user.password = "";
                    saveEventUsers();
                }, root.$handleFeatherError.bind(root));
            };


            const companyParams = computed(() => {
                return {
                    query: {
                        $sort: {
                            name: 1,
                        }
                    }
                }
            });
            const companyData = useFind({model: Company, params: companyParams});
            const companyOptions = computed(() => {
                return companyData.items.value.map(c => {
                    return {text: c.name, value: c.id}
                });
            });

            const changePermission = (inputPermission) => {
                switch (inputPermission) {
                    case ePermission.super:
                        showCompany.value = false;
                        showEvent.value = false;
                        break;
                    case ePermission.company:
                        showCompany.value = true;
                        showEvent.value = false;
                        break;
                    case ePermission.event:
                        showCompany.value = true;
                        showEvent.value = true;
                        break;
                    default:
                        return;
                }
                checkEventOfEventAdmin(inputPermission, selectedEvents.value.length);
            }

            const eventParams = computed(() => {
                return {
                    query: {
                        companyId: {
                            $in: [user.companyId],
                        },
                        $sort: {
                            name: 1,
                        }
                    }
                };
            });
            const {items: eventOptions, isPending: isEventPending} = useFind({
                model: Event,
                params: eventParams,
            });

            const eventHeaders = ref([
                {text: root.$t('event.name'), value: 'name'},
                {text: root.$t('event.start_date'), value: 'start_date'},
                {text: root.$t('event.end_date'), value: 'end_date'},
            ]);
            const selectedEvents = ref([]);
            const changeCompany = () => {
                selectedEvents.value = [];
            }

            const saveEventUsers = async () => {
                let addedEvents = selectedEvents.value;

                try {
                    await Promise.all([
                        ...addedEvents.map((e) => {
                            return new EventUser({userId: user.id, eventId: e.id}).save()
                        }),
                    ]);

                    root.$router.replace({name: 'user-list'});
                } catch (e) {
                    root.$handleFeatherError(e);
                }
            };

            const checkEventOfEventAdmin = (permission, selectEventLength) => {
                if (permission === ePermission.event && selectEventLength === 0 ) {
                    customFormValid.value = false;
                } else
                    customFormValid.value = true;
            }

            watch(selectedEvents, (selectedEvents, prevSelectedEvents) => {
                if (user)
                    checkEventOfEventAdmin(user.permissions, selectedEvents.length);
            }, {deep:true});

            return {
                user,
                emailRules,
                passwordRules,
                isFormValid,
                showPassword,
                saveUser,

                callbackIsValid,

                permissionOptions,
                // company: companyData.items,
                permissionRules,
                companyRules,

                companyOptions,
                eventOptions,

                showCompany,
                showEvent,

                changePermission,
                changeCompany,


                eventHeaders,
                isEventPending,
                search: ref(''),
                selectedEvents,
                DateTime,
                customFormValid,
            }
        },
    }
</script>